import type { AxiosError } from "axios";

import { CORRELATION_ID_HEADER } from "@/utils/logging/correlationId";
import {
    coercePublicErrorResponse,
    formatPublicErrorForDisplay,
} from "@/utils/errors/publicErrorContract";
import type { ApiError } from "@/types/api";

type ProblemDetailsLike = {
    type?: unknown;
    title?: unknown;
    status?: unknown;
    detail?: unknown;
    code?: unknown;
    errorCode?: unknown;
    correlationId?: unknown;
    instance?: unknown;
    error?: unknown;
    message?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

export const normalizeCorrelationId = (
    value: string | readonly string[] | undefined | unknown,
): string | undefined =>
    typeof value === "string"
        ? value
        : Array.isArray(value) && typeof value[0] === "string"
          ? value[0]
          : undefined;

export const normalizeApiError = (args: {
    axiosError: AxiosError;
    fallbackMessage: string;
}): ApiError => {
    const responseData = args.axiosError.response?.data;
    const problem = isRecord(responseData)
        ? (responseData as ProblemDetailsLike)
        : null;
    const responseCorrelationId = normalizeCorrelationId(
        args.axiosError.response?.headers?.[CORRELATION_ID_HEADER],
    );

    const detail =
        typeof problem?.detail === "string" ? problem.detail : undefined;
    const message =
        typeof problem?.message === "string" ? problem.message : undefined;
    const title =
        typeof problem?.title === "string" ? problem.title : undefined;
    const normalizedMessage =
        detail ?? message ?? title ?? args.fallbackMessage;
    const normalizedCode =
        typeof problem?.code === "string"
            ? problem.code
            : typeof problem?.errorCode === "string"
              ? problem.errorCode
              : typeof problem?.error === "string"
                ? problem.error
                : undefined;
    const normalizedCorrelationId =
        typeof problem?.correlationId === "string"
            ? problem.correlationId
            : responseCorrelationId;
    const normalizedInstance =
        typeof problem?.instance === "string" ? problem.instance : undefined;

    const publicError = coercePublicErrorResponse({
        status: args.axiosError.response?.status,
        data: responseData ?? normalizedMessage,
        correlationId: normalizedCorrelationId ?? null,
    });

    const displayMessage = formatPublicErrorForDisplay(publicError);

    return {
        status: args.axiosError.response?.status || 500,
        data: responseData,
        message: displayMessage,
        code: normalizedCode ?? publicError.errorCode,
        ...(normalizedCorrelationId
            ? { correlationId: normalizedCorrelationId }
            : {}),
        ...(normalizedInstance ? { instance: normalizedInstance } : {}),
    };
};
